import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  TrendingUp, AlertTriangle, HelpCircle, IndianRupee, Building2,
  Filter, CheckCircle,
  BarChart3, XCircle, Share2, Activity, History,
} from 'lucide-react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Legend,
} from 'recharts';
import { activityService } from '@core/supabase/services/activityService';
import * as XLSX from 'xlsx';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { supabase } from '@core/supabase/client';
import { clientService } from '@core/supabase/services/clientService';
import { ApplicationStatus, Client, REJECTION_REASON_LABELS, RejectionReasonCode, CANDIDATE_SOURCE_LABELS, CandidateSource } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { useToast } from '@shared/hooks/useToast';
import { ReportExportDropdown, type ReportExportFormat } from '@shared/components/ui/ReportExportDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ContextAiAssistModal } from '@shared/components/ai/ContextAiAssistModal';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { rechartsGridStroke, rechartsTooltipStyle } from '@shared/chart/rechartsTheme';
import { formatDateTimeGmt, formatDateOrDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { ReportsCannedSection } from '@modules/reports/ReportsCannedSection';

// -- Types -----------------------------------------------------
interface PipelineRow   {
  status: ApplicationStatus;
  total: number;
  with_client?: number;
  first_submission?: string;
  last_submission?: string;
}
interface NoShowRow     { candidate_name: string; job_title: string; client_name: string; no_show_at: string; assigned_to_name?: string | null }
interface PlacementRow  { candidate_name: string; job_title: string; client_name: string; placement_date: string | null; placement_fee: number | null; placed_by?: string | null }
interface ClientPerfRow { client_name: string; total_submitted: number; shortlisted: number; placed: number; rejected: number; no_shows: number; total_revenue: number | null }
interface RejectionRow  { rejection_reason_code: RejectionReasonCode; total: number; pct: number }
interface SourceRow     { source: string; total_candidates: number; total_applications: number; reached_shortlist: number; placed: number; placement_rate_pct: number }

/** Top-level report sections (UI tabs). */
type MainReportTab = 'canned' | 'overview' | 'activity' | 'revenue';

/** Legacy granular export / PDF slice keys (mapped from main tab for spreadsheets). */
type ReportTab = 'funnel' | 'noshows' | 'placements' | 'clients' | 'rejections' | 'sources';

interface ActivityFeedRow {
  id: string;
  entity: string;
  action: string;
  created_at: string;
  actor_name: string | null;
}

const STATUS_ORDER: ApplicationStatus[] = [
  'submitted','viewed_by_client','shortlisted',
  'interview_1_scheduled','interview_1_conducted',
  'interview_2_scheduled','interview_2_conducted',
  'offer_extended','offer_accepted','offer_rejected',
  'placed','rejected','no_show','on_hold','closed',
];

const STATUS_LABEL: Partial<Record<ApplicationStatus, string>> = {
  submitted: 'Submitted', viewed_by_client: 'Viewed by Client',
  shortlisted: 'Shortlisted', interview_1_scheduled: 'Interview 1 Scheduled',
  interview_1_conducted: 'Interview 1 Done', interview_2_scheduled: 'Interview 2 Scheduled',
  interview_2_conducted: 'Interview 2 Done', offer_extended: 'Offer Extended',
  offer_accepted: 'Offer Accepted', offer_rejected: 'Offer Rejected',
  placed: 'Placed', rejected: 'Rejected', no_show: 'No Show', on_hold: 'On Hold', closed: 'Closed',
};

/** ?25.A ? Chart palette: emerald-500 primary, emerald-300 secondary, zinc-300 baseline. */
const CHART_PRIMARY = '#10B981';   // emerald-500
const CHART_SECONDARY = '#6EE7B7'; // emerald-300
const CHART_BASELINE = '#D1D5DB';  // zinc-300

/** Distinct bar colors for pipeline stage charts (cycles if more stages than colors). */
const PIPELINE_STAGE_COLORS = [CHART_PRIMARY, CHART_SECONDARY, '#06b6d4', '#eab308', '#f97316', '#ec4899', '#14b8a6', '#a855f7', CHART_BASELINE, '#f43f5e', '#64748b'];

/** Light-theme tooltip/grid for off-screen export chart clones (readable in PDF/Word captures). */
const EXPORT_CHART_TOOLTIP = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  fontSize: 11,
  color: '#111827',
};
const EXPORT_GRID_STROKE = '#e5e7eb';
const EXPORT_AXIS_STROKE = '#64748b';

// -- Excel Export (multi-sheet: all tabs) ----------------------
function exportXlsxAll(
  filename: string,
  sheets: { name: string; rows: Record<string, unknown>[] }[]
) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    if (rows.length) {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
    }
  });
  if (wb.SheetNames.length) XLSX.writeFile(wb, filename);
}

function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Recruiter rollups for print/export (same logic as UI useMemo). */
function computeRecruiterPlacementsForExport(placements: PlacementRow[]) {
  const m = new Map<string, { placements: number; revenue: number }>();
  placements.forEach((p) => {
    const k = p.placed_by?.trim() || 'Unassigned';
    const cur = m.get(k) ?? { placements: 0, revenue: 0 };
    cur.placements += 1;
    cur.revenue += p.placement_fee ?? 0;
    m.set(k, cur);
  });
  return [...m.entries()]
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.revenue - a.revenue);
}

function computeActivityEntitiesForExport(activityFeed: ActivityFeedRow[]) {
  const map = new Map<string, number>();
  activityFeed.forEach((a) => map.set(a.entity, (map.get(a.entity) ?? 0) + 1));
  return [...map.entries()]
    .map(([entity, n]) => ({ entity, n }))
    .sort((a, b) => b.n - a.n);
}

/** CSS horizontal bar block for print/PDF (no canvas ? scales in browser print). */
function htmlHorizontalBars(
  title: string,
  subtitle: string,
  rows: { label: string; value: number }[],
  color: string,
  fixedMax?: number
): string {
  if (!rows.length) {
    return `<section style="margin-bottom:20px;page-break-inside:avoid"><h3 style="font-size:13px;margin-bottom:4px;color:#5a3fc0">${escapeHtml(title)}</h3><p style="font-size:10px;color:#666;margin:0 0 8px">${escapeHtml(subtitle)}</p><p style="font-size:11px;color:#999">No data.</p></section>`;
  }
  const max = fixedMax ?? Math.max(...rows.map((r) => r.value), 1);
  const bars = rows
    .map((r) => {
      const pct = Math.min(100, Math.round((r.value / max) * 100));
      const label = r.label.length > 44 ? `${r.label.slice(0, 42)}?` : r.label;
      const val =
        fixedMax === 100 ? `${Number(r.value).toFixed(1)}%` : r.value.toLocaleString('en-IN');
      return `<div style="margin-bottom:10px"><div style="display:flex;justify-content:space-between;align-items:baseline;font-size:11px;margin-bottom:3px"><span style="color:#333">${escapeHtml(label)}</span><span style="font-weight:600;color:#333">${escapeHtml(val)}</span></div><div style="height:14px;background:#e8e8ff;border-radius:8px;overflow:hidden;border:1px solid #e0d8ff"><div style="height:100%;width:${pct}%;background:${color};border-radius:7px"></div></div></div>`;
    })
    .join('');
  return `<section style="margin-bottom:20px;page-break-inside:avoid"><h3 style="font-size:13px;margin-bottom:4px;color:#5a3fc0">${escapeHtml(title)}</h3><p style="font-size:10px;color:#666;margin:0 0 12px">${escapeHtml(subtitle)}</p>${bars}</section>`;
}

function htmlActivityKpiStrip(args: {
  activityEvents: number;
  pipelineTotal: number;
  placements: number;
  revenueInr: number;
  noShows: number;
  rejections: number;
}): string {
  const box = (label: string, value: string, accent: string) =>
    `<div style="padding:10px 12px;border:1px solid #e0d8ff;border-radius:8px;background:#fafaff"><div style="font-size:9px;color:#666;text-transform:uppercase;letter-spacing:0.04em">${escapeHtml(label)}</div><div style="font-size:17px;font-weight:700;color:${accent};margin-top:4px">${escapeHtml(value)}</div></div>`;
  return `<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px">${box('Activity events', String(args.activityEvents), '#1f2937')}${box('Pipeline', String(args.pipelineTotal), '#6366f1')}${box('Placements', String(args.placements), '#16a34a')}</div><div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:8px">${box('Placement revenue (?)', args.revenueInr.toLocaleString('en-IN'), '#1f2937')}${box('No-shows', String(args.noShows), '#dc2626')}${box('Rejections', String(args.rejections), '#ea580c')}</div>`;
}

type OrgReportMeta = { orgName: string | null; orgId: string | null };

/** PNG data URLs captured from the same Recharts as the Reports UI (for PDF / Word / print). */
export type ReportChartSnapshots = {
  pipelineAreaPng?: string;
  pipelinePiePng?: string;
  revenueByClientBarPng?: string;
};

function htmlPipelineDashboardChartsFromSnapshots(areaUrl: string, pieUrl: string): string {
  const a = escapeHtml(areaUrl);
  const p = escapeHtml(pieUrl);
  return (
    `<section style="margin-bottom:20px;page-break-inside:avoid">` +
    `<h3 style="font-size:13px;margin-bottom:4px;color:#5a3fc0">Pipeline flow &amp; share by stage</h3>` +
    `<p style="font-size:10px;color:#666;margin:0 0 12px">Same charts as on the Organisation &amp; pipeline tab</p>` +
    `<div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:start">` +
    `<div style="border:1px solid #e5e7eb;border-radius:8px;padding:8px;background:#fff">` +
    `<p style="font-size:11px;font-weight:600;color:#374151;margin:0 0 6px">Pipeline flow</p>` +
    `<img src="${a}" alt="" style="width:100%;height:auto;display:block" />` +
    `</div>` +
    `<div style="border:1px solid #e5e7eb;border-radius:8px;padding:8px;background:#fff">` +
    `<p style="font-size:11px;font-weight:600;color:#374151;margin:0 0 6px">Share by stage</p>` +
    `<img src="${p}" alt="" style="width:100%;height:auto;display:block" />` +
    `</div></div></section>`
  );
}

function htmlRevenueBarChartFromSnapshot(url: string): string {
  const u = escapeHtml(url);
  return (
    `<section style="margin-bottom:20px;page-break-inside:avoid">` +
    `<h3 style="font-size:13px;margin-bottom:4px;color:#5a3fc0">Revenue by client</h3>` +
    `<p style="font-size:10px;color:#666;margin:0 0 12px">Top clients by placement revenue (?)</p>` +
    `<div style="border:1px solid #e5e7eb;border-radius:8px;padding:8px;background:#fff">` +
    `<img src="${u}" alt="" style="width:100%;height:auto;display:block" />` +
    `</div></section>`
  );
}

function buildReportFilename(ext: string, orgName: string | null) {
  const slug = (orgName ?? 'organisation').replace(/[^\w\u0900-\u0FFF]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48) || 'organisation';
  const date = new Date().toISOString().split('T')[0];
  return `${slug}_report_${date}.${ext}`;
}

const PDF_PART_H2 =
  'font-size:15px;margin:20px 0 10px;color:#5a3fc0;border-bottom:1px solid #e0d8ff;padding-bottom:6px;page-break-after:avoid';

// -- Print-to-PDF: three main parts (overview / activity / revenue) or one part --
function buildPrintHtml(
  pipeline: PipelineRow[],
  noShows: NoShowRow[],
  placements: PlacementRow[],
  clientPerf: ClientPerfRow[],
  rejections: RejectionRow[],
  sources: SourceRow[],
  activityFeed: ActivityFeedRow[],
  statusLabel: Partial<Record<string, string>>,
  rejectionLabel: (code: RejectionReasonCode) => string,
  sourceLabel: (src: string) => string,
  orgMeta: OrgReportMeta,
  pdfScope: MainReportTab | 'full' | null,
  chartSnapshots?: ReportChartSnapshots | null
): string {
  const sec = (title: string, tableHeaders: string, rows: string) =>
    `<section style="margin-bottom:24px;page-break-inside:avoid"><h3 style="font-size:13px;margin-bottom:8px;color:#5a3fc0">${title}</h3><table style="width:100%;border-collapse:collapse;font-size:11px"><thead><tr>${tableHeaders}</tr></thead><tbody>${rows}</tbody></table></section>`;

  const pipelineRows = pipeline.map((r) =>
    `<tr><td style="padding:6px 8px">${statusLabel[r.status] ?? r.status}</td><td style="padding:6px 8px;text-align:right">${r.total}</td></tr>`
  ).join('');
  const noshowsRows = noShows.map((r) =>
    `<tr><td style="padding:6px 8px">${escapeHtml(r.candidate_name)}</td><td style="padding:6px 8px">${escapeHtml(r.job_title)}</td><td style="padding:6px 8px">${escapeHtml(r.client_name)}</td><td style="padding:6px 8px">${escapeHtml(formatDateTimeGmt(r.no_show_at))}</td></tr>`
  ).join('');
  const placementsRows = placements.map((r) =>
    `<tr><td style="padding:6px 8px">${escapeHtml(r.candidate_name)}</td><td style="padding:6px 8px">${escapeHtml(r.client_name)}</td><td style="padding:6px 8px">${escapeHtml(r.placed_by ?? '?')}</td><td style="padding:6px 8px;text-align:right">${r.placement_fee ?? '?'}</td><td style="padding:6px 8px">${r.placement_date ? escapeHtml(formatDateOrDateTimeGmt(r.placement_date)) : '?'}</td></tr>`
  ).join('');
  const clientsRows = clientPerf.map((r) =>
    `<tr><td style="padding:6px 8px">${escapeHtml(r.client_name)}</td><td style="padding:6px 8px;text-align:right">${r.total_submitted}</td><td style="padding:6px 8px;text-align:right">${r.shortlisted}</td><td style="padding:6px 8px;text-align:right">${r.placed}</td><td style="padding:6px 8px;text-align:right">${r.total_revenue ?? '?'}</td></tr>`
  ).join('');
  const rejectRows = rejections.map((r) =>
    `<tr><td style="padding:6px 8px">${rejectionLabel(r.rejection_reason_code)}</td><td style="padding:6px 8px;text-align:right">${r.total}</td><td style="padding:6px 8px;text-align:right">${r.pct?.toFixed?.(1) ?? r.pct}%</td></tr>`
  ).join('');
  const sourceRows = sources.map((r) =>
    `<tr><td style="padding:6px 8px">${sourceLabel(r.source)}</td><td style="padding:6px 8px;text-align:right">${r.total_candidates}</td><td style="padding:6px 8px;text-align:right">${r.total_applications}</td><td style="padding:6px 8px;text-align:right">${r.placed}</td><td style="padding:6px 8px;text-align:right">${r.placement_rate_pct?.toFixed?.(1) ?? r.placement_rate_pct}%</td></tr>`
  ).join('');

  const activityLogRows = activityFeed.slice(0, 120).map((a) =>
    `<tr><td style="padding:5px 8px;font-size:10px">${escapeHtml(formatDateTimeGmt(a.created_at))}</td><td style="padding:5px 8px">${escapeHtml(a.actor_name ?? '?')}</td><td style="padding:5px 8px">${escapeHtml(a.entity)}</td><td style="padding:5px 8px">${escapeHtml(a.action)}</td></tr>`
  ).join('');

  const totalPipeline = pipeline.reduce((s, r) => s + r.total, 0);
  const totalRevenue = placements.reduce((s, r) => s + (r.placement_fee ?? 0), 0);
  const rejectionTotal = rejections.reduce((s, r) => s + r.total, 0);

  const recruiters = computeRecruiterPlacementsForExport(placements);
  const entities = computeActivityEntitiesForExport(activityFeed);
  const revenueByClient = [...clientPerf]
    .map((c) => ({ label: c.client_name, value: Number(c.total_revenue) || 0 }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
  const pipelineBars = pipeline
    .filter((r) => r.total > 0)
    .map((r) => ({ label: statusLabel[r.status] ?? String(r.status), value: r.total }));
  const rejectionBars = rejections.map((r) => ({
    label: rejectionLabel(r.rejection_reason_code),
    value: r.total,
  }));
  const sourceRateBars = sources.map((r) => ({
    label: sourceLabel(r.source),
    value: Number(r.placement_rate_pct) || 0,
  }));

  const hasPipelineSnap =
    chartSnapshots?.pipelineAreaPng &&
    chartSnapshots?.pipelinePiePng;
  const pipelineOverviewBlock = hasPipelineSnap
    ? htmlPipelineDashboardChartsFromSnapshots(
        chartSnapshots!.pipelineAreaPng!,
        chartSnapshots!.pipelinePiePng!
      )
    : htmlHorizontalBars(
        'Pipeline by stage (visual)',
        'Relative volume per stage ? mirrors the dashboard chart',
        pipelineBars,
        '#818cf8'
      );
  const revenueOverviewBlock = chartSnapshots?.revenueByClientBarPng
    ? htmlRevenueBarChartFromSnapshot(chartSnapshots.revenueByClientBarPng)
    : htmlHorizontalBars('Revenue by client (visual)', 'Top clients by placement revenue (?)', revenueByClient, '#8b5cf6');

  const visOverview = pipelineOverviewBlock + revenueOverviewBlock;

  const visActivity =
    htmlActivityKpiStrip({
      activityEvents: activityFeed.length,
      pipelineTotal: totalPipeline,
      placements: placements.length,
      revenueInr: totalRevenue,
      noShows: noShows.length,
      rejections: rejectionTotal,
    }) +
    htmlHorizontalBars(
      'Placements by recruiter (visual)',
      'Placement count per recruiter',
      recruiters.slice(0, 8).map((r) => ({ label: r.name, value: r.placements })),
      '#34d399'
    ) +
    htmlHorizontalBars(
      'Activity by entity (visual)',
      'Audit log events by entity type',
      entities.slice(0, 8).map((r) => ({ label: r.entity, value: r.n })),
      '#818cf8'
    ) +
    htmlHorizontalBars('Rejections by reason (visual)', 'Count per reason', rejectionBars, '#f87171') +
    htmlHorizontalBars('Placement rate by source (visual)', 'Percentage by source (RPT-040)', sourceRateBars, '#34d399', 100);

  const visRevenue =
    htmlHorizontalBars('Revenue by client (visual)', 'Placement revenue (?)', revenueByClient, '#818cf8') +
    htmlHorizontalBars(
      'Placements by recruiter (visual)',
      'Placement count per recruiter',
      recruiters.slice(0, 8).map((r) => ({ label: r.name, value: r.placements })),
      '#34d399'
    );

  const showP1Charts = pdfScope === 'full' || pdfScope === 'overview';
  const showP2Charts = pdfScope === 'full' || pdfScope === 'activity';
  const showP3Charts = pdfScope === 'full' || pdfScope === 'revenue';
  const orgBanner = `<div style="margin-bottom:16px;padding:10px 12px;background:#f8f6ff;border:1px solid #e0d8ff;border-radius:8px;font-size:12px;color:#333">
    <strong>Consultancy (branch)</strong>: ${escapeHtml(orgMeta.orgName ?? '?')}<br/>
    <span style="color:#666">Organisation ID:</span> <code style="font-size:11px">${escapeHtml(orgMeta.orgId ?? '?')}</code>
  </div>`;
  const summary = `${orgBanner}
    <section style="margin-bottom:24px;display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
      <div style="padding:12px;border:1px solid #eee;border-radius:8px"><strong>Pipeline</strong><p style="font-size:20px;margin:4px 0">${totalPipeline}</p></div>
      <div style="padding:12px;border:1px solid #eee;border-radius:8px"><strong>Placed</strong><p style="font-size:20px;margin:4px 0">${pipeline.find((r) => r.status === 'placed')?.total ?? 0}</p></div>
      <div style="padding:12px;border:1px solid #eee;border-radius:8px"><strong>Revenue (?)</strong><p style="font-size:20px;margin:4px 0">${totalRevenue.toLocaleString('en-IN')}</p></div>
    </section>`;

  const sFunnel = sec('Pipeline by stage', '<th style="text-align:left;padding:6px 8px;background:#f4f0ff">Stage</th><th style="text-align:right;padding:6px 8px;background:#f4f0ff">Count</th>', pipelineRows);
  const sNoshows = sec('No Shows', '<th style="padding:6px 8px;background:#f4f0ff">Candidate</th><th style="padding:6px 8px;background:#f4f0ff">Role</th><th style="padding:6px 8px;background:#f4f0ff">Client</th><th style="padding:6px 8px;background:#f4f0ff">Date</th>', noshowsRows);
  const sPlacements = sec(
    'Placements & fees',
    '<th style="padding:6px 8px;background:#f4f0ff">Candidate</th><th style="padding:6px 8px;background:#f4f0ff">Client</th><th style="padding:6px 8px;background:#f4f0ff">Recruiter</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Fee</th><th style="padding:6px 8px;background:#f4f0ff">Date</th>',
    placementsRows
  );
  const sClients = sec('Client Performance', '<th style="padding:6px 8px;background:#f4f0ff">Client</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Submitted</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Shortlisted</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Placed</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Revenue</th>', clientsRows);
  const sReject = sec('Rejection analytics', '<th style="padding:6px 8px;background:#f4f0ff">Reason</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Count</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">%</th>', rejectRows);
  const sSources = sec('Source effectiveness', '<th style="padding:6px 8px;background:#f4f0ff">Source</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Candidates</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Applications</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Placed</th><th style="padding:6px 8px;background:#f4f0ff;text-align:right">Placement %</th>', sourceRows);

  const activitySummary = `<table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:16px"><tbody>
    <tr><td style="padding:6px 8px;border:1px solid #eee"><strong>Activity events (export)</strong></td><td style="padding:6px 8px;border:1px solid #eee;text-align:right">${activityFeed.length}</td></tr>
    <tr><td style="padding:6px 8px;border:1px solid #eee"><strong>No-shows</strong></td><td style="padding:6px 8px;border:1px solid #eee;text-align:right">${noShows.length}</td></tr>
  </tbody></table>`;

  const sActivityLog =
    `<section style="margin-bottom:20px;page-break-inside:avoid"><h3 style="font-size:13px;margin-bottom:8px;color:#5a3fc0">Recent activity log</h3>` +
    `<table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>` +
    `<th style="padding:6px 8px;background:#f4f0ff;text-align:left">When</th>` +
    `<th style="padding:6px 8px;background:#f4f0ff">User</th>` +
    `<th style="padding:6px 8px;background:#f4f0ff">Entity</th>` +
    `<th style="padding:6px 8px;background:#f4f0ff">Action</th>` +
    `</tr></thead><tbody>${activityLogRows || '<tr><td colspan="4" style="padding:8px">No activity rows.</td></tr>'}</tbody></table></section>`;

  const part1 = `<h2 style="${PDF_PART_H2}">1. Organisation &amp; pipeline</h2>${summary}${showP1Charts ? visOverview : ''}${sFunnel}`;
  const part2 = `<div style="page-break-before:always"></div><h2 style="${PDF_PART_H2}">2. Activity &amp; contribution</h2>${showP2Charts ? visActivity : ''}${activitySummary}${sActivityLog}${sNoshows}${sReject}${sSources}`;
  const part3 = `<div style="page-break-before:always"></div><h2 style="${PDF_PART_H2}">3. Revenue</h2>${showP3Charts ? visRevenue : ''}${sPlacements}${sClients}`;

  const full = part1 + part2 + part3;
  if (pdfScope === 'overview') return part1;
  if (pdfScope === 'activity') return orgBanner + part2.replace('<div style="page-break-before:always"></div>', '');
  if (pdfScope === 'revenue') return orgBanner + part3.replace('<div style="page-break-before:always"></div>', '');
  return full;
}

/** Shared CSS for print/PDF and DOCX raster (same chart visuals as on-screen export HTML). */
const REPORT_EXPORT_CSS = `
  .report-export-root{font-family:Segoe UI,Arial,sans-serif;font-size:12px;color:#111;padding:24px;max-width:920px;margin:0 auto;box-sizing:border-box}
  .report-export-root table{width:100%;border-collapse:collapse;margin-top:8px}
  .report-export-root th{background:#f4f0ff;color:#5a3fc0;font-size:10px;text-transform:uppercase;padding:6px 8px;text-align:left}
  .report-export-root td{padding:6px 8px;border-bottom:1px solid #eee}
  .report-export-root h1{font-size:19px;margin-bottom:4px;color:#1f2937}
  .report-export-root .sub{color:#666;font-size:12px;margin-bottom:20px;line-height:1.45}
`;

function splitCanvasVertically(canvas: HTMLCanvasElement, maxChunkPx: number): HTMLCanvasElement[] {
  if (canvas.height <= maxChunkPx) return [canvas];
  const out: HTMLCanvasElement[] = [];
  for (let y = 0; y < canvas.height; y += maxChunkPx) {
    const h = Math.min(maxChunkPx, canvas.height - y);
    const c = document.createElement('canvas');
    c.width = canvas.width;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) continue;
    ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
    out.push(c);
  }
  return out.length ? out : [canvas];
}

async function canvasToPngUint8Array(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not render image'))), 'image/png');
  });
  return new Uint8Array(await blob.arrayBuffer());
}

/** Rasterizes the same HTML as print/PDF so Word shows matching charts and layout. */
async function exportFullReportToDocx(
  pipeline: PipelineRow[],
  noShows: NoShowRow[],
  placements: PlacementRow[],
  clientPerf: ClientPerfRow[],
  rejections: RejectionRow[],
  sources: SourceRow[],
  activityFeed: ActivityFeedRow[],
  statusLabel: Partial<Record<string, string>>,
  rejectionLabel: (code: RejectionReasonCode) => string,
  sourceLabel: (src: string) => string,
  orgMeta: OrgReportMeta,
  filename: string,
  chartSnapshots: ReportChartSnapshots | null
): Promise<void> {
  const content = buildPrintHtml(
    pipeline,
    noShows,
    placements,
    clientPerf,
    rejections,
    sources,
    activityFeed,
    statusLabel,
    rejectionLabel,
    sourceLabel,
    orgMeta,
    'full',
    chartSnapshots
  );
  const dateStr = formatDateTimeGmt(new Date().toISOString());
  const fullSub = `Full export: Organisation &amp; pipeline, Activity, Revenue ? tables and chart-style visuals ? ${dateStr}`;
  const htmlInner = `<style>${REPORT_EXPORT_CSS}</style>
    <div class="report-export-root" style="max-width:780px;width:780px;margin:0">
    <h1>Organisation report (all tabs)</h1>
    <p class="sub">${fullSub}</p>
    ${content}
    </div>`;

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;background:#fff';
  container.innerHTML = htmlInner;
  document.body.appendChild(container);

  try {
    const html2canvas = (await import('html2canvas')).default;
    const { Document, Packer, Paragraph, ImageRun } = await import('docx');
    await (document.fonts?.ready ?? Promise.resolve());

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 780,
    });

    const chunks = splitCanvasVertically(canvas, 3600);
    const paragraphs: import('docx').Paragraph[] = [];
    const targetW = 600;

    for (const chunk of chunks) {
      const pngData = await canvasToPngUint8Array(chunk);
      const targetH = Math.max(1, Math.round((targetW / chunk.width) * chunk.height));
      paragraphs.push(
        new Paragraph({
          children: [
            new ImageRun({
              type: 'png',
              data: pngData,
              transformation: { width: targetW, height: targetH },
            }),
          ],
        })
      );
    }

    const doc = new Document({
      sections: [{ children: paragraphs }],
    });
    const blob = await Packer.toBlob(doc);
    const a = document.createElement('a');
    const name = filename.endsWith('.docx') ? filename : `${filename}.docx`;
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } finally {
    document.body.removeChild(container);
  }
}

function printReport(
  pipeline: PipelineRow[],
  noShows: NoShowRow[],
  placements: PlacementRow[],
  clientPerf: ClientPerfRow[],
  rejections: RejectionRow[],
  sources: SourceRow[],
  activityFeed: ActivityFeedRow[],
  statusLabel: Partial<Record<string, string>>,
  rejectionLabel: (code: RejectionReasonCode) => string,
  sourceLabel: (src: string) => string,
  orgMeta: OrgReportMeta,
  pdfScope: MainReportTab | 'full' | null,
  chartSnapshots?: ReportChartSnapshots | null
) {
  const win = window.open('', '_blank');
  if (!win) return;
  const scope = pdfScope === null ? 'full' : pdfScope;
  const content = buildPrintHtml(
    pipeline,
    noShows,
    placements,
    clientPerf,
    rejections,
    sources,
    activityFeed,
    statusLabel,
    rejectionLabel,
    sourceLabel,
    orgMeta,
    scope,
    chartSnapshots
  );
  const titleOrg = escapeHtml(orgMeta.orgName ?? 'Organisation');
  const dateStr = formatDateTimeGmt(new Date().toISOString());
  const fullTitle = scope === 'full' ? 'Organisation report (all tabs)' : 'Organisation report';
  const fullSub =
    scope === 'full'
      ? `Full export: Organisation &amp; pipeline, Activity, Revenue ? tables and chart-style visuals ? ${dateStr}`
      : `Generated by GoGrabJob Business ? ${dateStr}`;
  win.document.write(`<!DOCTYPE html><html><head><title>${titleOrg} ? Report</title>
    <style>
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      ${REPORT_EXPORT_CSS}
    </style></head><body class="report-export-root">
    <h1>${fullTitle}</h1>
    <p class="sub">${fullSub}</p>
    ${content}</body></html>`);
  win.document.close();
  win.print();
}

// -- Th cell (global `.ggj-data-table` styles padding/typography) -----------
function Th({ children, right, className }: { children?: React.ReactNode; right?: boolean; className?: string }) {
  const content =
    children === undefined || children === null || children === false
      ? null
      : typeof children === 'string' || typeof children === 'number'
        ? `[${children}]`
        : children;
  return (
    <th className={[right ? 'text-right' : 'text-left', className].filter(Boolean).join(' ')}>
      {content}
    </th>
  );
}

export function ReportsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();

  const [mainTab, setMainTab] = useState<MainReportTab>('canned');
  const [loading, setLoading] = useState(false);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedRow[]>([]);

  // Filters
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [clients,      setClients]      = useState<Client[]>([]);

  const [pipeline,    setPipeline]    = useState<PipelineRow[]>([]);
  const [noShows,     setNoShows]     = useState<NoShowRow[]>([]);
  const [placements,  setPlacements]  = useState<PlacementRow[]>([]);
  const [clientPerf,  setClientPerf]  = useState<ClientPerfRow[]>([]);
  const [rejections,  setRejections]  = useState<RejectionRow[]>([]);
  const [sources,     setSources]     = useState<SourceRow[]>([]);
  const [reportsAiOpen, setReportsAiOpen] = useState(false);
  const [reportsInsight, setReportsInsight] = useState('');
  const [docxExporting, setDocxExporting] = useState(false);
  /** ?25.A ? report settings drawer state */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDateRange, setSettingsDateRange] = useState<'30d' | '90d' | '12m' | 'ytd'>('30d');
  const [settingsGroupBy, setSettingsGroupBy] = useState<'status' | 'client' | 'recruiter'>('status');
  const [settingsCompare, setSettingsCompare] = useState<'none' | 'prev_period' | 'prev_year'>('none');

  const exportPipelineAreaRef = useRef<HTMLDivElement>(null);
  const exportPipelinePieRef = useRef<HTMLDivElement>(null);
  const exportRevenueBarRef = useRef<HTMLDivElement>(null);

  const reportsMetricsContext = useMemo(() => {
    const pipe = pipeline.map((r) => `${STATUS_LABEL[r.status] ?? r.status}: ${r.total}`).join('\n');
    const topReject = rejections.slice(0, 5).map((r) => `${REJECTION_REASON_LABELS[r.rejection_reason_code]}: ${r.total} (${r.pct}%)`).join('\n');
    const src = sources.slice(0, 6).map((r) => `${r.source}: apps ${r.total_applications}, placed ${r.placed}`).join('\n');
    return [
      '? Pipeline ?',
      pipe || '?',
      '\n? Placements (count) ?',
      String(placements.length),
      '\n? No-shows (rows) ?',
      String(noShows.length),
      '\n? Top rejections ?',
      topReject || '?',
      '\n? Sources (sample) ?',
      src || '?',
    ].join('\n');
  }, [pipeline, rejections, sources, placements.length, noShows.length]);

  /** Smooth area ? ordered stages (theme line; avoids many separate bars). */
  const pipelineAreaData = useMemo(
    () =>
      STATUS_ORDER.map((st) => {
        const row = pipeline.find((p) => p.status === st);
        const full = STATUS_LABEL[st] ?? String(st);
        const short = full.replace(/ Interview /g, ' Int. ').replace('Scheduled', 'Sched.');
        return {
          label: short.length > 18 ? `${short.slice(0, 16)}?` : short,
          count: row?.total ?? 0,
        };
      }),
    [pipeline]
  );

  /** Share of pipeline by stage (pie ? top visual, fewer bars). */
  const pipelinePieData = useMemo(() => {
    const rows = pipeline.filter((r) => r.total > 0).map((r) => ({
      name: (STATUS_LABEL[r.status] ?? r.status).length > 24 ? `${(STATUS_LABEL[r.status] ?? r.status).slice(0, 22)}?` : (STATUS_LABEL[r.status] ?? r.status),
      value: r.total,
    }));
    if (rows.length <= 8) return rows;
    const top = rows.slice(0, 7).sort((a, b) => b.value - a.value);
    const rest = rows.slice(7).reduce((s, r) => s + r.value, 0);
    return [...top, { name: 'Other stages', value: rest }];
  }, [pipeline]);

  const placementByRecruiter = useMemo(() => {
    const m = new Map<string, { placements: number; revenue: number }>();
    placements.forEach((p) => {
      const k = p.placed_by?.trim() || 'Unassigned';
      const cur = m.get(k) ?? { placements: 0, revenue: 0 };
      cur.placements += 1;
      cur.revenue += p.placement_fee ?? 0;
      m.set(k, cur);
    });
    return [...m.entries()]
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [placements]);

  /** Top clients by revenue ? shared by Organisation & pipeline + Revenue tab bar charts. */
  const revenueByClientBarData = useMemo(
    () =>
      [...clientPerf]
        .map((c) => ({
          name: c.client_name.length > 18 ? `${c.client_name.slice(0, 16)}?` : c.client_name,
          rev: Number(c.total_revenue) || 0,
        }))
        .filter((d) => d.rev > 0)
        .sort((a, b) => b.rev - a.rev)
        .slice(0, 8),
    [clientPerf]
  );

  const placementByRecruiterBarData = useMemo(
    () =>
      placementByRecruiter.slice(0, 8).map((r) => ({
        name: r.name.length > 18 ? `${r.name.slice(0, 16)}?` : r.name,
        count: r.placements,
      })),
    [placementByRecruiter]
  );

  const activityEntitySummary = useMemo(() => {
    const m = new Map<string, number>();
    activityFeed.forEach((a) => {
      m.set(a.entity, (m.get(a.entity) ?? 0) + 1);
    });
    return [...m.entries()]
      .map(([entity, n]) => ({ entity, n }))
      .sort((a, b) => b.n - a.n);
  }, [activityFeed]);

  /** Top entity types for Activity tab bar chart. */
  const activityEntityBarData = useMemo(
    () =>
      activityEntitySummary.slice(0, 8).map(({ entity, n }) => ({
        name: entity.length > 20 ? `${entity.slice(0, 18)}?` : entity,
        events: n,
      })),
    [activityEntitySummary]
  );

  const activityPipelineTotal = useMemo(
    () => pipeline.reduce((s, r) => s + r.total, 0),
    [pipeline]
  );
  const activityPlacementRevenue = useMemo(
    () => placements.reduce((s, r) => s + (r.placement_fee ?? 0), 0),
    [placements]
  );
  const activityRejectionTotal = useMemo(
    () => rejections.reduce((s, r) => s + r.total, 0),
    [rejections]
  );

  /** Snapshots of the same Recharts as the dashboard for PDF/Word (not CSS bar fallbacks). */
  const captureReportChartSnapshots = useCallback(async (): Promise<ReportChartSnapshots | null> => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const opts = { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true };
      await new Promise((r) => setTimeout(r, 450));
      const out: ReportChartSnapshots = {};
      if (exportPipelineAreaRef.current) {
        out.pipelineAreaPng = (await html2canvas(exportPipelineAreaRef.current, opts)).toDataURL('image/png');
      }
      if (pipelinePieData.length > 0 && exportPipelinePieRef.current) {
        out.pipelinePiePng = (await html2canvas(exportPipelinePieRef.current, opts)).toDataURL('image/png');
      }
      if (revenueByClientBarData.length > 0 && exportRevenueBarRef.current) {
        out.revenueByClientBarPng = (await html2canvas(exportRevenueBarRef.current, opts)).toDataURL('image/png');
      }
      return out;
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [pipelinePieData.length, revenueByClientBarData.length]);

  // Load client list for filter dropdown
  useEffect(() => {
    if (!user?.orgId) return;
    clientService.list(user.orgId).then(setClients);
  }, [user?.orgId]);

  // Load ALL reports for org overview + comprehensive export
  const fetchAllReports = useCallback(async () => {
    if (!user?.orgId) return;
    const orgId = user.orgId;
    setLoading(true);
    try {
      const [pipeRes, noshowsRes, placementsRes, clientPerfRes, rejectRes, sourcesRes] = await Promise.all([
        supabase.from('ggj_b2b_report_pipeline').select('*').eq('org_id', orgId),
        supabase.from('ggj_b2b_report_noshows').select('*').eq('org_id', orgId),
        supabase.from('ggj_b2b_report_placements').select('*').eq('org_id', orgId),
        supabase.from('ggj_b2b_report_client_perf').select('*').eq('org_id', orgId),
        supabase.from('ggj_b2b_report_rejections').select('*').eq('org_id', orgId).order('total', { ascending: false }),
        supabase.from('ggj_b2b_report_source_effectiveness').select('*').eq('org_id', orgId).order('total_applications', { ascending: false }),
      ]);
      const pipeData = pipeRes.data ?? [];
      setPipeline(STATUS_ORDER.map((s) => pipeData.find((r: PipelineRow) => r.status === s)).filter(Boolean) as PipelineRow[]);
      setNoShows((noshowsRes.data ?? []) as NoShowRow[]);
      setPlacements((placementsRes.data ?? []) as PlacementRow[]);
      setClientPerf((clientPerfRes.data ?? []) as ClientPerfRow[]);
      setRejections((rejectRes.data ?? []) as RejectionRow[]);
      setSources((sourcesRes.data ?? []) as SourceRow[]);
    } finally {
      setLoading(false);
    }
  }, [user?.orgId]);

  useEffect(() => { fetchAllReports(); }, [fetchAllReports]);

  useEffect(() => {
    if (!user?.orgId) return;
    activityService
      .listByOrg(user.orgId, { limit: 500 })
      .then((rows) => {
        const mapped: ActivityFeedRow[] = (rows as any[]).map((r) => ({
          id: r.id,
          entity: r.entity,
          action: r.action,
          created_at: r.created_at,
          actor_name: r.actor?.display_name ?? null,
        }));
        setActivityFeed(mapped);
      })
      .catch(() => setActivityFeed([]));
  }, [user?.orgId]);

  const fetchReport = useCallback(async (t: ReportTab) => {
    if (!user?.orgId) return;
    setLoading(true);
    const orgId = user.orgId;
    try {
      if (t === 'funnel') {
        const { data } = await supabase
          .from('ggj_b2b_report_pipeline')
          .select('status,total,with_client,first_submission,last_submission')
          .eq('org_id', orgId);
        const sorted = STATUS_ORDER
          .map((s) => (data ?? []).find((r: PipelineRow) => r.status === s))
          .filter(Boolean) as PipelineRow[];
        setPipeline(sorted);
      }
      if (t === 'noshows') {
        let q = supabase.from('ggj_b2b_report_noshows').select('*').eq('org_id', orgId);
        if (clientFilter) q = q.eq('client_name', clients.find((c) => c.id === clientFilter)?.name ?? '');
        if (dateFrom)     q = q.gte('no_show_at', dateFrom);
        if (dateTo)       q = q.lte('no_show_at', dateTo + 'T23:59:59');
        const { data } = await q;
        setNoShows((data ?? []) as NoShowRow[]);
      }
      if (t === 'placements') {
        let q = supabase.from('ggj_b2b_report_placements').select('*').eq('org_id', orgId);
        if (clientFilter) q = q.eq('client_name', clients.find((c) => c.id === clientFilter)?.name ?? '');
        if (dateFrom)     q = q.gte('placement_date', dateFrom);
        if (dateTo)       q = q.lte('placement_date', dateTo);
        const { data } = await q;
        setPlacements((data ?? []) as PlacementRow[]);
      }
      if (t === 'clients') {
        const { data } = await supabase.from('ggj_b2b_report_client_perf').select('*').eq('org_id', orgId);
        setClientPerf((data ?? []) as ClientPerfRow[]);
      }
      if (t === 'rejections') {
        const { data } = await supabase
          .from('ggj_b2b_report_rejections')
          .select('rejection_reason_code,total,pct')
          .eq('org_id', orgId)
          .order('total', { ascending: false });
        setRejections((data ?? []) as RejectionRow[]);
      }
      if (t === 'sources') {
        const { data } = await supabase
          .from('ggj_b2b_report_source_effectiveness')
          .select('source,total_candidates,total_applications,reached_shortlist,placed,placement_rate_pct')
          .eq('org_id', orgId)
          .order('total_applications', { ascending: false });
        setSources((data ?? []) as SourceRow[]);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.orgId, clientFilter, dateFrom, dateTo, clients]);

  useEffect(() => {
    if (!user?.orgId) return;
    fetchReport('noshows');
    fetchReport('placements');
    fetchReport('rejections');
    fetchReport('sources');
  }, [user?.orgId, clientFilter, dateFrom, dateTo, fetchReport]);

  const handleExportXlsx = () => {
    const totalPipeline = pipeline.reduce((s, r) => s + r.total, 0);
    const totalRevenue = placements.reduce((s, r) => s + (r.placement_fee ?? 0), 0);
    const rejectionSum = rejections.reduce((s, r) => s + r.total, 0);
    const recruiters = computeRecruiterPlacementsForExport(placements);
    const entities = computeActivityEntitiesForExport(activityFeed);
    const revenueByClient = [...clientPerf]
      .map((c) => ({ Client: c.client_name, Revenue_INR: Number(c.total_revenue) || 0 }))
      .filter((r) => r.Revenue_INR > 0)
      .sort((a, b) => b.Revenue_INR - a.Revenue_INR)
      .slice(0, 12);
    const summary = [
      { Metric: 'Organisation (consultancy)', Value: user?.orgName ?? '?' },
      { Metric: 'Organisation ID', Value: user?.orgId ?? '?' },
      { Metric: 'Total in pipeline', Value: totalPipeline },
      { Metric: 'Placed', Value: pipeline.find((r) => r.status === 'placed')?.total ?? 0 },
      { Metric: 'Total revenue (?)', Value: totalRevenue },
      { Metric: 'Clients', Value: clientPerf.length },
      { Metric: 'No-shows', Value: noShows.length },
    ];
    const orPlaceholder = (rows: Record<string, unknown>[]) => (rows.length ? rows : [{ _note: 'No data for this series' }]);
    const sheets = [
      { name: 'Summary', rows: summary },
      {
        name: 'KPI_All_Tabs',
        rows: [
          {
            Activity_events: activityFeed.length,
            Pipeline_total: totalPipeline,
            Placements: placements.length,
            Revenue_INR: totalRevenue,
            No_shows: noShows.length,
            Rejections_total: rejectionSum,
            Sources_tracked: sources.length,
          },
        ],
      },
      { name: 'Pipeline Funnel', rows: pipeline.map((r) => ({ Stage: STATUS_LABEL[r.status] ?? r.status, Count: r.total })) },
      { name: 'Chart_Revenue_by_client', rows: orPlaceholder(revenueByClient as Record<string, unknown>[]) },
      {
        name: 'Chart_Recruiter_placements',
        rows: orPlaceholder(
          recruiters.map((r) => ({ Recruiter: r.name, Placements: r.placements, Revenue_INR: r.revenue })) as Record<string, unknown>[]
        ),
      },
      {
        name: 'Chart_Activity_entity',
        rows: orPlaceholder(entities.map((e) => ({ Entity: e.entity, Events: e.n })) as Record<string, unknown>[]),
      },
      {
        name: 'Chart_Reject_reason',
        rows: orPlaceholder(
          rejections.map((r) => ({
            Reason: REJECTION_REASON_LABELS[r.rejection_reason_code] ?? r.rejection_reason_code,
            Count: r.total,
            Pct: r.pct,
          })) as Record<string, unknown>[]
        ),
      },
      {
        name: 'Chart_Source_place_pct',
        rows: orPlaceholder(
          sources.map((r) => ({
            Source: CANDIDATE_SOURCE_LABELS[r.source as CandidateSource] ?? r.source,
            Placement_pct: r.placement_rate_pct,
            Placed: r.placed,
            Applications: r.total_applications,
          })) as Record<string, unknown>[]
        ),
      },
      { name: 'No Shows', rows: noShows.map((r) => ({ Candidate: r.candidate_name, Role: r.job_title, Client: r.client_name, Date: r.no_show_at })) },
      { name: 'Placements', rows: placements.map((r) => ({ Candidate: r.candidate_name, Role: r.job_title, Client: r.client_name, Fee: r.placement_fee, Date: r.placement_date })) },
      { name: 'Client Performance', rows: clientPerf.map((r) => ({ Client: r.client_name, Submitted: r.total_submitted, Shortlisted: r.shortlisted, Placed: r.placed, Rejected: r.rejected, NoShows: r.no_shows, Revenue: r.total_revenue })) },
      {
        name: 'Rejections',
        rows: rejections.map((r) => ({
          Reason: REJECTION_REASON_LABELS[r.rejection_reason_code] ?? r.rejection_reason_code,
          Code: r.rejection_reason_code,
          Count: r.total,
          Pct: r.pct,
        })),
      },
      {
        name: 'Source ROI',
        rows: sources.map((r) => ({
          Source: CANDIDATE_SOURCE_LABELS[r.source as CandidateSource] ?? r.source,
          Candidates: r.total_candidates,
          Applications: r.total_applications,
          Shortlist: r.reached_shortlist,
          Placed: r.placed,
          Placement_pct: r.placement_rate_pct,
        })),
      },
      {
        name: 'Activity log',
        rows: activityFeed.map((a) => ({
          When: a.created_at,
          User: a.actor_name ?? '',
          Entity: a.entity,
          Action: a.action,
        })),
      },
    ];
    exportXlsxAll(buildReportFilename('xlsx', user?.orgName ?? null), sheets);
  };

  const handleReportExport = async (fmt: ReportExportFormat) => {
    const orgMeta: OrgReportMeta = { orgName: user?.orgName ?? null, orgId: user?.orgId ?? null };
    if (fmt === 'pdf') {
      const snaps = await captureReportChartSnapshots();
      printReport(
        pipeline,
        noShows,
        placements,
        clientPerf,
        rejections,
        sources,
        activityFeed,
        STATUS_LABEL,
        (code) => REJECTION_REASON_LABELS[code] ?? code,
        (src) => CANDIDATE_SOURCE_LABELS[src as CandidateSource] ?? src,
        orgMeta,
        'full',
        snaps
      );
      return;
    }
    if (fmt === 'docx') {
      try {
        setDocxExporting(true);
        const snaps = await captureReportChartSnapshots();
        await exportFullReportToDocx(
          pipeline,
          noShows,
          placements,
          clientPerf,
          rejections,
          sources,
          activityFeed,
          STATUS_LABEL,
          (code) => REJECTION_REASON_LABELS[code] ?? code,
          (src) => CANDIDATE_SOURCE_LABELS[src as CandidateSource] ?? src,
          orgMeta,
          buildReportFilename('docx', user?.orgName ?? null),
          snaps
        );
      } catch (e) {
        console.error(e);
        showToast({ title: 'Could not generate Word export. Try PDF or Excel, or use another browser.', kind: 'error' });
      } finally {
        setDocxExporting(false);
      }
      return;
    }
    if (fmt === 'xlsx') {
      if (!perms.canExportReports) {
        showToast({ title: 'You do not have permission to export Excel. Ask an admin or senior recruiter.', kind: 'error' });
        return;
      }
      handleExportXlsx();
    }
  };

  const showFilters = mainTab === 'activity' || mainTab === 'revenue';

  if (!perms.canViewReports) {
    return (
      <div className="card text-center py-16">
        <p className="vscode-text-secondary">You don't have permission to view reports.</p>
      </div>
    );
  }

  return (
    <div>
      {toast}
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Reports', href: '/reports' }]}
        breadcrumbRowEnd={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* ?25.A ? settings drawer trigger */}
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-1.5 shrink-0"
              onClick={() => setSettingsOpen(true)}
              title="Report settings"
            >
              <Filter className="w-3.5 h-3.5" />
              Settings
            </button>
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-1.5 shrink-0"
              onClick={() => window.open(objectHistoryUrl('reports'), '_blank', 'noopener,noreferrer')}
              title="Object history"
            >
              <History className="w-3.5 h-3.5" />
              History
            </button>
            <ReportExportDropdown
              onExport={(fmt) => void handleReportExport(fmt)}
              allowSpreadsheet={perms.canExportReports}
              docxBusy={docxExporting}
            />
          </div>
        }
      />

      {/* ?25.A ? 360px settings drawer */}
      {settingsOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/30"
            onClick={() => setSettingsOpen(false)}
            aria-hidden
          />
          <aside
            role="dialog"
            aria-label="Report settings"
            className="fixed inset-y-0 right-0 z-40 flex w-[360px] flex-col border-l vscode-border vscode-bg-primary shadow-2xl"
          >
            <div className="flex items-center justify-between border-b px-4 py-3 vscode-border">
              <p className="text-[15px] font-semibold vscode-text-primary">Report settings</p>
              <button
                type="button"
                className="rounded p-1 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                aria-label="Close settings"
                onClick={() => setSettingsOpen(false)}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-6 p-4">
              <div>
                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                  Date range
                </label>
                <div className="flex flex-wrap gap-2">
                  {(['30d', '90d', '12m', 'ytd'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setSettingsDateRange(v)}
                      className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                        settingsDateRange === v
                          ? 'border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'vscode-border vscode-text-secondary hover:vscode-bg-hover'
                      }`}
                    >
                      {v === '30d' ? 'Last 30 days' : v === '90d' ? 'Last 90 days' : v === '12m' ? 'Last 12 months' : 'Year to date'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                  Group by
                </label>
                <div className="flex flex-wrap gap-2">
                  {(['status', 'client', 'recruiter'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setSettingsGroupBy(v)}
                      className={`rounded-full border px-3 py-1 text-[12px] font-medium capitalize transition-colors ${
                        settingsGroupBy === v
                          ? 'border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'vscode-border vscode-text-secondary hover:vscode-bg-hover'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                  Compare window
                </label>
                <div className="flex flex-wrap gap-2">
                  {(['none', 'prev_period', 'prev_year'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setSettingsCompare(v)}
                      className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                        settingsCompare === v
                          ? 'border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'vscode-border vscode-text-secondary hover:vscode-bg-hover'
                      }`}
                    >
                      {v === 'none' ? 'No comparison' : v === 'prev_period' ? 'Previous period' : 'Previous year'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t px-4 py-3 vscode-border">
              <button
                type="button"
                className="w-full rounded-[8px] bg-emerald-500 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-emerald-600"
                onClick={() => setSettingsOpen(false)}
              >
                Apply settings
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Optional AI insight section - only show if there's content */}
      {reportsInsight ? (
        <div className="mb-4 rounded-xl border vscode-border vscode-bg-secondary p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <p className="text-xs font-semibold vscode-text-primary">AI Insights</p>
            </div>
            <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={() => setReportsAiOpen(true)}>
              Regenerate
            </button>
          </div>
          <pre className="text-xs whitespace-pre-wrap vscode-text-secondary max-h-48 overflow-y-auto rounded-lg vscode-bg-tertiary p-3">
            {reportsInsight}
          </pre>
        </div>
      ) : null}

      <ContextAiAssistModal
        open={reportsAiOpen}
        onClose={() => setReportsAiOpen(false)}
        orgId={user?.orgId}
        feature="ai_assist_reports_narrative"
        preset="reports_predictive_narrative"
        title="AI ? reports narrative"
        contextHint={reportsMetricsContext}
        original={reportsInsight}
        onApply={(text, mode) =>
          setReportsInsight(
            mode === 'replace' ? text : reportsInsight.trim() ? `${reportsInsight.trim()}\n\n---\n\n${text}` : text
          )
        }
      />

      {/* -- Main report tabs ? same pattern as Account (Settings ? ggj-cand-tablist) - */}
      <div className="mb-5">
        <nav className="ggj-cand-tablist mb-0" aria-label="Report section tabs">
          {([
            { id: 'canned' as const, label: 'Quick reports' },
            { id: 'overview' as const, label: 'Organisation & pipeline' },
            { id: 'activity' as const, label: 'Activity' },
            { id: 'revenue' as const, label: 'Revenue' },
          ]).map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={mainTab === id}
              id={`reports-tab-${id}`}
              onClick={() => setMainTab(id)}
              className={`ggj-cand-tab ${mainTab === id ? 'ggj-cand-tab--active' : ''}`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* -- Filters (activity / revenue) ------------------------ */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 mb-5 p-3 vscode-bg-secondary border vscode-border rounded-xl">
          <Filter className="w-4 h-4 vscode-text-tertiary flex-shrink-0" />
          {clients.length > 0 && (
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="input text-xs py-1.5 max-w-[180px]"
            >
              <option value="">All Clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="input text-xs py-1.5 w-36" placeholder="From" />
            <span className="vscode-text-tertiary text-xs">to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="input text-xs py-1.5 w-36" placeholder="To" />
          </div>
          {(clientFilter || dateFrom || dateTo) && (
            <button onClick={() => { setClientFilter(''); setDateFrom(''); setDateTo(''); }}
              className="text-xs vscode-text-tertiary hover:vscode-text-primary">
              Clear
            </button>
          )}
        </div>
      )}

      {mainTab === 'canned' ? (
        <div id="report-content">
          <ReportsCannedSection orgId={user?.orgId} canExportCsv={perms.canExportReports} />
        </div>
      ) : loading ? (
        <FullPageSpinner />
      ) : (
        <div id="report-content">

          {/* -- Overview: organisation + pipeline (theme charts, minimal bars) - */}
          {mainTab === 'overview' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold vscode-text-primary flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-brand-400" /> Organisation status
                  </h2>
                  {user?.orgName ? (
                    <p className="text-[11px] vscode-text-tertiary mt-1">Consultancy: <span className="vscode-text-secondary font-medium">{user.orgName}</span></p>
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="card py-3 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_40%,transparent)] shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Pipeline</p>
                  <p className="text-xl font-bold vscode-text-primary tabular-nums">{pipeline.reduce((s, r) => s + r.total, 0)}</p>
                </div>
                <div className="card py-3 rounded-xl ring-1 ring-emerald-500/20 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Placed</p>
                  <p className="text-xl font-bold text-emerald-400 tabular-nums">{pipeline.find((r) => r.status === 'placed')?.total ?? 0}</p>
                </div>
                <div className="card py-3 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_40%,transparent)] shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Revenue</p>
                  <p className="text-xl font-bold vscode-text-primary tabular-nums">?{placements.reduce((s, r) => s + (r.placement_fee ?? 0), 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="card py-3 rounded-xl ring-1 ring-brand-500/25 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Clients</p>
                  <p className="text-xl font-bold text-brand-400 tabular-nums">{clientPerf.length}</p>
                </div>
                <div className="card py-3 rounded-xl border-red-800/40 ring-1 ring-red-500/15 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">No-shows</p>
                  <p className="text-xl font-bold text-red-400 tabular-nums">{noShows.length}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]" style={{ minHeight: 260 }}>
                  <p className="text-xs font-medium vscode-text-secondary mb-1">Pipeline flow</p>
                  <p className="text-[10px] vscode-text-tertiary mb-2">Candidates per stage (ordered) ? area curve matches your theme</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={pipelineAreaData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="repPipeArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART_PRIMARY} stopOpacity={0.45} />
                          <stop offset="100%" stopColor={CHART_PRIMARY} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" interval={0} angle={-35} textAnchor="end" height={70} />
                      <YAxis tick={{ fontSize: 10 }} stroke="var(--vscode-text-tertiary)" allowDecimals={false} width={36} />
                      <Tooltip contentStyle={rechartsTooltipStyle} cursor={false} />
                      <Area type="monotone" dataKey="count" stroke={CHART_PRIMARY} strokeWidth={2} fill="url(#repPipeArea)" dot={{ r: 2, fill: CHART_PRIMARY }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]" style={{ minHeight: 260 }}>
                  <p className="text-xs font-medium vscode-text-secondary mb-1">Share by stage</p>
                  <p className="text-[10px] vscode-text-tertiary mb-2">Distribution ? donut view (fewer bars than a full bar wall)</p>
                  {pipelinePieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={pipelinePieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={48}
                          outerRadius={78}
                          paddingAngle={2}
                        >
                          {pipelinePieData.map((_, i) => (
                            <Cell key={i} fill={PIPELINE_STAGE_COLORS[i % PIPELINE_STAGE_COLORS.length]} stroke="var(--vscode-editor-background)" />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={rechartsTooltipStyle} cursor={false} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm vscode-text-tertiary py-8 text-center">No pipeline data.</p>
                  )}
                </div>
              </div>

              {revenueByClientBarData.length > 0 && (
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]" style={{ minHeight: 220 }}>
                  <p className="text-xs font-medium vscode-text-secondary mb-1">Revenue by client</p>
                  <p className="text-[10px] vscode-text-tertiary mb-2">Top clients by placement revenue ? shown here with pipeline overview</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={revenueByClientBarData.slice(0, 5)}
                      margin={{ top: 8, right: 8, left: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 9 }}
                        stroke="var(--vscode-text-tertiary)"
                        interval={0}
                        angle={-28}
                        textAnchor="end"
                        height={72}
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        stroke="var(--vscode-text-tertiary)"
                        width={44}
                        tickFormatter={(v) => Number(v).toLocaleString('en-IN')}
                      />
                      {/* ?25.A ? suppress hover tooltip covering bar; caption below instead */}
                      <Tooltip content={() => null} cursor={false} />
                      <Bar dataKey="rev" fill={CHART_PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={36} />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="mt-1 text-[11px] vscode-text-tertiary">
                    Top {Math.min(5, revenueByClientBarData.length)} clients ?{' '}
                    total ?{revenueByClientBarData.slice(0, 5).reduce((s, r) => s + r.rev, 0).toLocaleString('en-IN')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* -- No Shows -------------------------------------- */}
          {mainTab === 'activity' && (
            <div className="space-y-10">
              <div>
                <h2 className="text-sm font-semibold vscode-text-primary flex items-center gap-2">
                  <Activity className="w-4 h-4 text-brand-400" /> Activity & contribution
                </h2>
                <p className="text-[11px] vscode-text-tertiary mt-1">
                  Report snapshot: KPIs, charts for workload and entity mix, then detailed tables (recruiters, log, no-shows, rejections, sources).
                </p>
              </div>

              {/* -- Activity report: 6 KPI cards ----------------- */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="card py-3 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_40%,transparent)]">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider leading-tight">Activity events</p>
                  <p className="text-lg font-bold vscode-text-primary tabular-nums mt-0.5">{activityFeed.length}</p>
                  <p className="text-[9px] vscode-text-tertiary mt-1">Audit log rows</p>
                </div>
                <div className="card py-3 rounded-xl ring-1 ring-brand-500/20 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider leading-tight">Pipeline</p>
                  <p className="text-lg font-bold text-brand-400 tabular-nums mt-0.5">{activityPipelineTotal}</p>
                  <p className="text-[9px] vscode-text-tertiary mt-1">Apps in funnel</p>
                </div>
                <div className="card py-3 rounded-xl ring-1 ring-emerald-500/25 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider leading-tight">Placements</p>
                  <p className="text-lg font-bold text-emerald-400 tabular-nums mt-0.5">{placements.length}</p>
                  <p className="text-[9px] vscode-text-tertiary mt-1">In period / org</p>
                </div>
                <div className="card py-3 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider leading-tight">Placement revenue</p>
                  <p className="text-lg font-bold vscode-text-primary tabular-nums mt-0.5">?{activityPlacementRevenue.toLocaleString('en-IN')}</p>
                  <p className="text-[9px] vscode-text-tertiary mt-1">Fees recorded</p>
                </div>
                <div className="card py-3 rounded-xl border-red-800/40 ring-1 ring-red-500/15 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider leading-tight">No-shows</p>
                  <p className="text-lg font-bold text-red-400 tabular-nums mt-0.5">{noShows.length}</p>
                  <p className="text-[9px] vscode-text-tertiary mt-1">Filtered range</p>
                </div>
                <div className="card py-3 rounded-xl border-orange-900/30 ring-1 ring-orange-500/15 shadow-sm">
                  <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider leading-tight">Rejections</p>
                  <p className="text-lg font-bold text-orange-400 tabular-nums mt-0.5">{activityRejectionTotal}</p>
                  <p className="text-[9px] vscode-text-tertiary mt-1">Total rejected</p>
                </div>
              </div>

              {/* -- Charts: recruiter workload + entity mix ------- */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]">
                  <p className="text-xs font-medium vscode-text-secondary mb-0.5">Placements by recruiter</p>
                  <p className="text-[10px] vscode-text-tertiary mb-3">Vertical bars ? placement count</p>
                  {placementByRecruiterBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={placementByRecruiterBarData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 9 }}
                          stroke="var(--vscode-text-tertiary)"
                          interval={0}
                          angle={-22}
                          textAnchor="end"
                          height={68}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="var(--vscode-text-tertiary)" width={28} />
                        <Tooltip content={() => null} cursor={false} />
                        <Bar dataKey="count" fill={CHART_SECONDARY} radius={[4, 4, 0, 0]} maxBarSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm vscode-text-tertiary py-12 text-center">No placement rows to chart.</p>
                  )}
                </div>
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]">
                  <p className="text-xs font-medium vscode-text-secondary mb-0.5">Activity by entity</p>
                  <p className="text-[10px] vscode-text-tertiary mb-3">Top entity types by event volume (log)</p>
                  {activityEntityBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={activityEntityBarData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 9 }}
                          stroke="var(--vscode-text-tertiary)"
                          interval={0}
                          angle={-22}
                          textAnchor="end"
                          height={68}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="var(--vscode-text-tertiary)" width={32} />
                        <Tooltip content={() => null} cursor={false} />
                        <Bar dataKey="events" fill={CHART_PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm vscode-text-tertiary py-12 text-center">No activity events yet.</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                <div className="card overflow-hidden p-0 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] flex flex-col min-h-[220px]">
                  <p className="text-xs font-medium vscode-text-secondary px-4 pt-3 pb-2 border-b vscode-border shrink-0">Contribution by recruiter (tabular)</p>
                  {placementByRecruiter.length > 0 ? (
                    <div className="overflow-x-auto overflow-y-auto max-h-96 flex-1">
                      <table className="ggj-data-table">
                        <thead>
                          <tr className="border-b vscode-border">
                            <Th>Recruiter</Th>
                            <Th right>Placements</Th>
                            <Th right>Revenue (?)</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {placementByRecruiter.map((r) => (
                            <tr key={r.name}>
                              <td className="px-4 py-2.5 font-medium vscode-text-primary">{r.name}</td>
                              <td className="px-4 py-2.5 text-right vscode-text-secondary">{r.placements}</td>
                              <td className="px-4 py-2.5 text-right text-emerald-400 font-medium">?{r.revenue.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="px-4 py-10 text-sm vscode-text-tertiary text-center flex-1 flex items-center justify-center">No placement rows to split by recruiter.</p>
                  )}
                </div>
                <div className="card overflow-hidden p-0 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] flex flex-col min-h-[220px] max-h-96">
                  <p className="text-xs font-medium vscode-text-secondary px-4 pt-3 pb-2 border-b vscode-border shrink-0">Recent activity (log)</p>
                  <div className="overflow-y-auto flex-1 min-h-0 overscroll-contain">
                    {activityFeed.length === 0 ? (
                      <p className="px-4 py-10 text-sm vscode-text-tertiary text-center">No activity events yet.</p>
                    ) : (
                      <table className="ggj-data-table text-sm">
                        <thead>
                          <tr className="border-b vscode-border">
                            <Th>When</Th>
                            <Th>User</Th>
                            <Th>Entity</Th>
                            <Th>Action</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityFeed.slice(0, 100).map((a) => (
                            <tr key={a.id}>
                              <td className="px-4 py-2 text-xs vscode-text-tertiary whitespace-nowrap">{formatDateTimeGmt(a.created_at)}</td>
                              <td className="px-4 py-2 vscode-text-secondary">{a.actor_name ?? '?'}</td>
                              <td className="px-4 py-2 vscode-text-secondary">{a.entity}</td>
                              <td className="px-4 py-2 vscode-text-primary">{a.action}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              {activityEntitySummary.length > 0 && (
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_30%,transparent)]">
                  <p className="text-xs font-medium vscode-text-secondary mb-2">Entity breakdown (quick counts)</p>
                  <div className="flex flex-wrap gap-2">
                    {activityEntitySummary.slice(0, 12).map(({ entity, n }) => (
                      <span key={entity} className="text-xs px-2.5 py-1 rounded-full vscode-bg-secondary border vscode-border vscode-text-secondary">
                        {entity}: <strong className="vscode-text-primary">{n}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <section className="mt-8 space-y-4">
                <div>
                  <h3 className="text-xs font-semibold vscode-text-tertiary uppercase tracking-wider mb-1">Outcomes & sources</h3>
                  <p className="text-[10px] vscode-text-tertiary">
                    Four cards in a 2?2 grid (side by side on large screens): no-shows, rejections, source KPIs, placement rate. Full source table follows.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* 1 ? No-show records */}
                  <div className="card overflow-hidden p-0 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] flex flex-col min-h-[260px] max-h-[min(420px,55vh)]">
                    <p className="text-xs font-medium vscode-text-secondary px-4 pt-3 pb-2 border-b vscode-border shrink-0">No-show records</p>
                    <p className="text-[10px] vscode-text-tertiary px-4 pt-2 pb-1">Filters apply when set.</p>
                    <div className="overflow-auto flex-1 min-h-0 px-0 pb-2">
                      {noShows.length === 0
                        ? (
                          <div className="px-4 py-10 text-center">
                            <AlertTriangle className="w-10 h-10 vscode-text-tertiary mx-auto mb-2" />
                            <p className="vscode-text-tertiary text-sm">No no-shows recorded.</p>
                            <p className="vscode-text-tertiary text-xs mt-1">Try adjusting filters or date range.</p>
                          </div>
                        )
                        : (
                          <table className="ggj-data-table">
                            <thead><tr className="border-b vscode-border"><Th>Candidate</Th><Th>Role</Th><Th>Client</Th><Th>No-show date</Th><Th>Assigned to</Th></tr></thead>
                            <tbody>
                              {noShows.map((row, i) => (
                                <tr key={i}>
                                  <td className="px-4 py-2.5 font-medium vscode-text-primary">{row.candidate_name}</td>
                                  <td className="px-4 py-2.5 vscode-text-secondary">{row.job_title}</td>
                                  <td className="px-4 py-2.5 vscode-text-secondary">{row.client_name}</td>
                                  <td className="px-4 py-2.5 vscode-text-secondary text-xs">{formatDateTimeGmt(row.no_show_at)}</td>
                                  <td className="px-4 py-2.5 vscode-text-tertiary text-xs">{row.assigned_to_name ?? '?'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                    </div>
                  </div>

                  {/* 2 ? Rejection chart + tabular */}
                  <div className="card overflow-hidden p-0 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] flex flex-col min-h-[260px] max-h-[min(420px,55vh)]">
                    <p className="text-xs font-medium vscode-text-secondary px-4 pt-3 pb-2 border-b vscode-border shrink-0">Rejection reasons (tabular)</p>
                    <p className="text-[10px] vscode-text-tertiary px-4 pt-2 pb-1">Chart below ? totals in KPI row above.</p>
                    {rejections.length > 0 && (
                      <div className="px-2 pb-2 shrink-0 border-b vscode-border">
                        <p className="text-[10px] vscode-text-tertiary px-2 mb-1">Rejection breakdown (chart)</p>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart
                            data={rejections.map((r) => ({
                              name: REJECTION_REASON_LABELS[r.rejection_reason_code] ?? r.rejection_reason_code,
                              count: r.total,
                              pct: Math.round(r.pct),
                            }))}
                            margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} />
                            <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" interval={0} angle={-18} textAnchor="end" height={56} />
                            <YAxis tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" width={28} />
                            <Tooltip
                              contentStyle={rechartsTooltipStyle}
                              cursor={false}
                              formatter={(v: number, _: string, props: { payload?: { pct?: number } }) =>
                                [`${v} (${props.payload?.pct ?? 0}%)`, 'Count']
                              }
                            />
                            <Bar dataKey="count" fill="#f87171" radius={[4, 4, 0, 0]} maxBarSize={28} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    <div className="overflow-auto flex-1 min-h-0">
                      {rejections.length === 0
                        ? (
                          <div className="px-4 py-10 text-center">
                            <XCircle className="w-10 h-10 vscode-text-tertiary mx-auto mb-2" />
                            <p className="vscode-text-tertiary text-sm">No rejection data yet.</p>
                          </div>
                        )
                        : (
                          <table className="ggj-data-table">
                            <thead>
                              <tr className="border-b vscode-border">
                                <Th>Reason</Th>
                                <Th right>Count</Th>
                                <Th right>% of rejected</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {rejections.map((row, i) => (
                                <tr key={i}>
                                  <td className="px-4 py-2.5 font-medium vscode-text-primary">
                                    {REJECTION_REASON_LABELS[row.rejection_reason_code] ?? row.rejection_reason_code}
                                  </td>
                                  <td className="px-4 py-2.5 text-right text-red-400 font-medium">{row.total}</td>
                                  <td className="px-4 py-2.5 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <div className="w-20 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                        <div
                                          className="h-full bg-red-500 rounded-full"
                                          style={{ width: `${Math.min(100, row.pct)}%` }}
                                        />
                                      </div>
                                      <span className="vscode-text-secondary text-xs w-10 text-right">{Math.round(row.pct)}%</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                    </div>
                  </div>

                  {/* 3 ? Source effectiveness (KPIs) */}
                  <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] flex flex-col min-h-[260px]">
                    <p className="text-xs font-medium vscode-text-secondary mb-1">Source effectiveness</p>
                    <p className="text-[10px] vscode-text-tertiary mb-3">Summary counts across candidate sources.</p>
                    <div className="grid grid-cols-2 gap-2 flex-1 content-start">
                      <div className="rounded-lg border vscode-border bg-[var(--vscode-bg-secondary)]/80 px-3 py-2.5">
                        <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Sources</p>
                        <p className="text-xl font-bold vscode-text-primary tabular-nums">{sources.length}</p>
                      </div>
                      <div className="rounded-lg border vscode-border bg-[var(--vscode-bg-secondary)]/80 px-3 py-2.5">
                        <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Candidates</p>
                        <p className="text-xl font-bold text-brand-400 tabular-nums">{sources.reduce((s, r) => s + r.total_candidates, 0)}</p>
                      </div>
                      <div className="rounded-lg border vscode-border bg-[var(--vscode-bg-secondary)]/80 px-3 py-2.5">
                        <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Applications</p>
                        <p className="text-xl font-bold vscode-text-primary tabular-nums">{sources.reduce((s, r) => s + r.total_applications, 0)}</p>
                      </div>
                      <div className="rounded-lg border vscode-border bg-[var(--vscode-bg-secondary)]/80 px-3 py-2.5">
                        <p className="text-[10px] vscode-text-tertiary uppercase tracking-wider">Placed</p>
                        <p className="text-xl font-bold text-emerald-400 tabular-nums">{sources.reduce((s, r) => s + r.placed, 0)}</p>
                      </div>
                    </div>
                  </div>

                  {/* 4 ? Placement rate by source */}
                  <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] flex flex-col min-h-[260px]">
                    <p className="text-xs font-medium vscode-text-secondary mb-0.5">Placement rate by source (RPT-040)</p>
                    <p className="text-[10px] vscode-text-tertiary mb-2">% placement by source label.</p>
                    {sources.length > 0 ? (
                      <div className="w-full" style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart
                            data={sources.map((r) => {
                              const label = CANDIDATE_SOURCE_LABELS[r.source as CandidateSource] ?? r.source;
                              return {
                                name: label.length > 22 ? `${label.slice(0, 20)}?` : label,
                                rate: Math.round(Number(r.placement_rate_pct) * 10) / 10,
                                placed: r.placed,
                                apps: r.total_applications,
                              };
                            })}
                            margin={{ top: 4, right: 12, left: 0, bottom: 4 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} />
                            <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" interval={0} angle={-18} textAnchor="end" height={56} />
                            <YAxis
                              tick={{ fontSize: 9 }}
                              stroke="var(--vscode-text-tertiary)"
                              domain={[0, 100]}
                              tickFormatter={(v) => `${v}%`}
                              width={32}
                            />
                            <Tooltip
                              contentStyle={rechartsTooltipStyle}
                              cursor={false}
                              formatter={(v: number, _n: string, props: { payload?: { placed?: number; apps?: number } }) => {
                                const p = props.payload;
                                return [`${v}% (${p?.placed ?? 0} placed / ${p?.apps ?? 0} apps)`, 'Placement rate'];
                              }}
                              labelFormatter={(label) => String(label ?? '')}
                            />
                            <Bar dataKey="rate" fill={CHART_SECONDARY} radius={[4, 4, 0, 0]} name="Placement %" maxBarSize={24} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-sm vscode-text-tertiary py-8 text-center flex-1">No source data for chart.</p>
                    )}
                  </div>
                </div>

                {/* Full width ? source detail table */}
                <div className="card overflow-hidden p-0 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]">
                  <p className="text-xs font-medium vscode-text-secondary px-4 pt-3 pb-2 border-b vscode-border">Candidate sources (tabular)</p>
                  {sources.length === 0
                    ? (
                      <div className="px-4 py-12 text-center">
                        <Share2 className="w-12 h-12 vscode-text-tertiary mx-auto mb-3" />
                        <p className="vscode-text-tertiary text-sm">No source data yet ? add a structured source when creating candidates.</p>
                      </div>
                    )
                    : (
                      <div className="overflow-x-auto">
                        <table className="ggj-data-table">
                          <thead>
                            <tr className="border-b vscode-border">
                              <Th>Source</Th>
                              <Th right>Candidates</Th>
                              <Th right>Applications</Th>
                              <Th right>Shortlisted</Th>
                              <Th right>Placed</Th>
                              <Th right>Placement %</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {sources.map((row, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2.5 font-medium vscode-text-primary">
                                  {CANDIDATE_SOURCE_LABELS[row.source as CandidateSource] ?? row.source}
                                </td>
                                <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.total_candidates}</td>
                                <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.total_applications}</td>
                                <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.reached_shortlist}</td>
                                <td className="px-4 py-2.5 text-right text-emerald-400 font-medium">{row.placed}</td>
                                <td className="px-4 py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <div className="w-16 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-emerald-500 rounded-full"
                                        style={{ width: `${Math.min(100, row.placement_rate_pct)}%` }}
                                      />
                                    </div>
                                    <span className="vscode-text-secondary text-xs w-10 text-right">{Number(row.placement_rate_pct).toFixed(1)}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                </div>
              </section>
            </div>
          )}

          {mainTab === 'revenue' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-sm font-semibold vscode-text-primary flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-emerald-400" /> Revenue
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)]" style={{ minHeight: 240 }}>
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-xs font-medium vscode-text-secondary">Revenue by client</p>
                      <p className="text-[10px] vscode-text-tertiary">Vertical columns ? top {Math.min(8, revenueByClientBarData.length)} with revenue</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-md vscode-bg-secondary border vscode-border vscode-text-tertiary">
                      {(clientFilter || dateFrom || dateTo) ? 'Filters on' : 'All data'}
                    </span>
                  </div>
                  {revenueByClientBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart
                        data={revenueByClientBarData}
                        margin={{ top: 8, right: 8, left: 4, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 9 }}
                          stroke="var(--vscode-text-tertiary)"
                          interval={0}
                          angle={-28}
                          textAnchor="end"
                          height={76}
                        />
                        <YAxis
                          tick={{ fontSize: 10 }}
                          stroke="var(--vscode-text-tertiary)"
                          width={48}
                          tickFormatter={(v) => Number(v).toLocaleString('en-IN')}
                        />
                        <Tooltip
                          contentStyle={rechartsTooltipStyle}
                          cursor={false}
                          formatter={(v: number) => [`?${Number(v).toLocaleString('en-IN')}`, 'Revenue']}
                        />
                        <Bar dataKey="rev" fill={CHART_PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={36} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm vscode-text-tertiary py-10 text-center">No client revenue in reporting data yet.</p>
                  )}
                </div>

                <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] border-green-900/20" style={{ minHeight: 240 }}>
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-xs font-medium vscode-text-secondary">Placements by recruiter</p>
                      <p className="text-[10px] vscode-text-tertiary">
                        Count per recruiter ? <span className="text-emerald-400 font-semibold tabular-nums">{placements.length}</span> total placements
                      </p>
                    </div>
                  </div>
                  {placementByRecruiterBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart
                        data={placementByRecruiterBarData}
                        margin={{ top: 8, right: 8, left: 4, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 9 }}
                          stroke="var(--vscode-text-tertiary)"
                          interval={0}
                          angle={-28}
                          textAnchor="end"
                          height={76}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="var(--vscode-text-tertiary)" width={36} />
                        <Tooltip contentStyle={rechartsTooltipStyle} cursor={false} formatter={(v: number) => [v, 'Placements']} />
                        <Bar dataKey="count" fill={CHART_SECONDARY} radius={[4, 4, 0, 0]} maxBarSize={36} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm vscode-text-tertiary py-10 text-center">No placement rows to chart for this period.</p>
                  )}
                </div>
              </div>

              <section className="mt-2">
                <h3 className="text-xs font-semibold vscode-text-tertiary uppercase tracking-wider mb-3">Client performance</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="card py-3">
                  <p className="text-xs vscode-text-tertiary uppercase tracking-wider">Clients</p>
                  <p className="text-2xl font-bold vscode-text-primary">{clientPerf.length}</p>
                </div>
                <div className="card py-3">
                  <p className="text-xs vscode-text-tertiary uppercase tracking-wider">Total submitted</p>
                  <p className="text-2xl font-bold text-brand-400">{clientPerf.reduce((s, r) => s + r.total_submitted, 0)}</p>
                </div>
                <div className="card py-3">
                  <p className="text-xs vscode-text-tertiary uppercase tracking-wider">Total placed</p>
                  <p className="text-2xl font-bold text-green-400">{clientPerf.reduce((s, r) => s + r.placed, 0)}</p>
                </div>
                <div className="card py-3">
                  <p className="text-xs vscode-text-tertiary uppercase tracking-wider">Total revenue</p>
                  <p className="text-2xl font-bold vscode-text-primary">?{clientPerf.reduce((s, r) => s + (Number(r.total_revenue) ?? 0), 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
              <div className="card overflow-hidden p-0">
              {clientPerf.length === 0
                ? (
                  <div className="px-4 py-12 text-center">
                    <Building2 className="w-12 h-12 vscode-text-tertiary mx-auto mb-3" />
                    <p className="vscode-text-tertiary text-sm">No client data yet.</p>
                  </div>
                )
                : (
                  <table className="ggj-data-table">
                    <thead>
                      <tr className="border-b vscode-border">
                        <Th>Client</Th><Th right>Sent</Th><Th right>Shortlisted</Th>
                        <Th right>Placed</Th><Th right>No Shows</Th><Th right>Revenue</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientPerf.map((row, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2.5 font-medium vscode-text-primary">{row.client_name}</td>
                          <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.total_submitted}</td>
                          <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.shortlisted}</td>
                          <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.placed}</td>
                          <td className="px-4 py-2.5 text-right vscode-text-secondary">{row.no_shows}</td>
                          <td className="px-4 py-2.5 text-right">
                            {row.total_revenue
                              ? <span className="text-green-400 font-medium">?{Number(row.total_revenue).toLocaleString('en-IN')}</span>
                              : <span className="vscode-text-tertiary">?</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
              </div>
              </section>
            </div>
          )}
        </div>
      )}

      {/* Off-screen Recharts clones (light theme) ? captured for PDF/Word so exports match the dashboard */}
      <div
        className="fixed left-[-9999px] top-0 pointer-events-none overflow-hidden"
        style={{ width: 900, zIndex: -1 }}
        aria-hidden
      >
        <div
          ref={exportPipelineAreaRef}
          className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
          style={{ width: 420, height: 280 }}
        >
          <p className="text-xs font-medium text-gray-800 mb-1">Pipeline flow</p>
          <p className="text-[10px] text-gray-500 mb-2">Candidates per stage (ordered)</p>
          <div style={{ width: 396, height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={pipelineAreaData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="repPipeAreaExport" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_PRIMARY} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={CHART_PRIMARY} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={EXPORT_GRID_STROKE} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: EXPORT_AXIS_STROKE }}
                  stroke={EXPORT_AXIS_STROKE}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: EXPORT_AXIS_STROKE }}
                  stroke={EXPORT_AXIS_STROKE}
                  allowDecimals={false}
                  width={36}
                />
                <Tooltip contentStyle={EXPORT_CHART_TOOLTIP} cursor={false} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#a5b4fc"
                  strokeWidth={2}
                  fill="url(#repPipeAreaExport)"
                  dot={{ r: 2, fill: '#818cf8' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        {pipelinePieData.length > 0 ? (
          <div
            ref={exportPipelinePieRef}
            className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mt-2"
            style={{ width: 420, height: 300 }}
          >
            <p className="text-xs font-medium text-gray-800 mb-1">Share by stage</p>
            <p className="text-[10px] text-gray-500 mb-2">Distribution ? donut view</p>
            <div style={{ width: 396, height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pipelinePieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={2}
                  >
                    {pipelinePieData.map((_, i) => (
                      <Cell key={i} fill={PIPELINE_STAGE_COLORS[i % PIPELINE_STAGE_COLORS.length]} stroke="#ffffff" />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={EXPORT_CHART_TOOLTIP} cursor={false} />
                  <Legend wrapperStyle={{ fontSize: 10, color: '#374151' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}
        {revenueByClientBarData.length > 0 ? (
          <div
            ref={exportRevenueBarRef}
            className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mt-2"
            style={{ width: 820, minHeight: 280 }}
          >
            <p className="text-xs font-medium text-gray-800 mb-1">Revenue by client</p>
            <p className="text-[10px] text-gray-500 mb-2">Top clients by placement revenue</p>
            <div style={{ width: 792, height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByClientBarData.slice(0, 5)} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={EXPORT_GRID_STROKE} vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 9, fill: EXPORT_AXIS_STROKE }}
                    stroke={EXPORT_AXIS_STROKE}
                    interval={0}
                    angle={-28}
                    textAnchor="end"
                    height={72}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: EXPORT_AXIS_STROKE }}
                    stroke={EXPORT_AXIS_STROKE}
                    width={44}
                    tickFormatter={(v) => Number(v).toLocaleString('en-IN')}
                  />
                  <Tooltip
                    contentStyle={EXPORT_CHART_TOOLTIP}
                    cursor={false}
                    formatter={(v: number) => [`?${Number(v).toLocaleString('en-IN')}`, 'Revenue']}
                  />
                  <Bar dataKey="rev" fill={CHART_PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}
      </div>

    </div>
  );
}
